import React, { useState, useEffect } from 'react';
import { BarChart3, Clock, X } from 'lucide-react';
import { Modal } from '../ui';
import { estadisticasService, MinutosPorJornada } from '../../services/EstadisticasService';

/**
 * Props del componente ModalMinutosJugador
 */
interface ModalMinutosJugadorProps {
  isOpen: boolean;
  onClose: () => void;
  jugadorId: number;
  jugadorNombre: string;
  temporadaId: number;
  temporadaNombre: string;
}

/**
 * Modal que muestra gráfica de minutos por jornada del jugador vs promedio del equipo
 */
const ModalMinutosJugador: React.FC<ModalMinutosJugadorProps> = ({
  isOpen,
  onClose,
  jugadorId,
  jugadorNombre,
  temporadaId,
  temporadaNombre
}) => {
  const [datosMinutos, setDatosMinutos] = useState<MinutosPorJornada[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carga los datos de minutos por jornada
   */
  useEffect(() => {
    if (isOpen && jugadorId && temporadaId) {
      cargarDatosMinutos();
    }
  }, [isOpen, jugadorId, temporadaId]);

  /**
   * Obtiene los datos de minutos por jornada del jugador y promedio del equipo
   */
  const cargarDatosMinutos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await estadisticasService.obtenerMinutosPorJornada(jugadorId, temporadaId);
      setDatosMinutos(data);
    } catch (err) {
      console.error('Error al cargar datos de minutos:', err);
      setError('Error al cargar los datos de minutos');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calcula la altura de la barra basada en los minutos (máximo 60)
   */
  const calcularAlturaBarra = (minutos: number): number => {
    return Math.min((minutos / 60) * 100, 100);
  };

  /**
   * Calcula la posición del punto del promedio
   */
  const calcularPosicionPromedio = (promedio: number): number => {
    return Math.min((promedio / 60) * 100, 100);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="bg-secondary-900 rounded-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-700">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-600 rounded-full p-2">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white font-poppins">
                Minutos por Jornada
              </h2>
              <p className="text-secondary-400 font-poppins text-sm">
                {jugadorNombre} - {temporadaNombre}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 p-6 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-secondary-400 font-poppins">Cargando datos...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-secondary-500 mb-4 mx-auto" />
              <p className="text-secondary-400 font-poppins text-lg mb-2">
                Error al cargar los datos
              </p>
              <p className="text-secondary-500 font-poppins text-sm">
                {error}
              </p>
            </div>
          ) : datosMinutos.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-secondary-500 mb-4 mx-auto" />
              <p className="text-secondary-400 font-poppins text-lg mb-2">
                No hay datos disponibles
              </p>
              <p className="text-secondary-500 font-poppins text-sm">
                No se encontraron registros de minutos para esta temporada
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Leyenda */}
              <div className="flex items-center justify-center space-x-6 bg-secondary-800/50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-primary-500 rounded"></div>
                  <span className="text-secondary-300 font-poppins text-sm">
                    Minutos del jugador
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-secondary-300 font-poppins text-sm">
                    Promedio del equipo
                  </span>
                </div>
              </div>

              {/* Gráfica */}
              <div className="bg-secondary-800/30 rounded-lg p-6">
                <div className="relative flex">
                  {/* Eje Y - Etiquetas de minutos (Fijo) */}
                  <div className="h-80 flex flex-col justify-between text-xs text-secondary-400 font-poppins shrink-0 mr-4">
                    <span>60</span>
                    <span>45</span>
                    <span>30</span>
                    <span>15</span>
                    <span>0</span>
                  </div>

                  {/* Área de la gráfica con Scroll Horizontal */}
                  <div className="flex-1 overflow-x-auto pb-2">
                    <div style={{ minWidth: `${Math.max(100, datosMinutos.length * 60)}px` }} className="relative">
                      {/* Líneas de referencia horizontales (Background) */}
                      <div className="absolute inset-0 h-80 w-full z-0">
                        {[0, 25, 50, 75, 100].map((percentage) => (
                          <div
                            key={percentage}
                            className="absolute w-full border-t border-secondary-700/50"
                            style={{ top: `${percentage}%` }}
                          />
                        ))}
                      </div>

                      {/* Contenedor principal de la gráfica */}
                      <div className="relative z-10">
                        {/* Contenedor de barras y puntos */}
                        <div className="relative h-80 bg-secondary-800/30 rounded-lg p-4">
                          
                          {/* Capa 1: Barras del jugador (z-10) */}
                          <div className="absolute inset-4 flex items-end justify-between z-10 pointer-events-none">
                             {datosMinutos.map((dato) => {
                               const alturaBarra = calcularAlturaBarra(dato.minutosJugador);
                               return (
                                 <div key={`bar-${dato.jornada}`} className="relative flex-1 flex flex-col items-center h-full pointer-events-auto group/bar">
                                   <div className="relative w-8 h-full flex flex-col justify-end">
                                      <div
                                        className="w-full bg-primary-500 rounded-t transition-all duration-300 hover:bg-primary-400 relative"
                                        style={{ height: `${alturaBarra}%` }}
                                      >
                                        {/* Tooltip de la barra */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover/bar:opacity-100 transition-opacity bg-secondary-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none z-50">
                                          {dato.minutosJugador} min
                                        </div>
                                      </div>
                                   </div>
                                 </div>
                               );
                             })}
                          </div>

                          {/* Capa 2: Línea conectando los promedios (z-20) */}
                          <div className="absolute inset-4 z-20 pointer-events-none">
                             <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                               <polyline
                                 fill="none"
                                 stroke="#f97316"
                                 strokeWidth="2"
                                 vectorEffect="non-scaling-stroke"
                                 points={datosMinutos.map((dato, index) => {
                                   const totalItems = datosMinutos.length;
                                   const itemWidth = 100 / totalItems;
                                   const xPercent = (index * itemWidth) + (itemWidth / 2);
                                   const yPercent = 100 - calcularPosicionPromedio(dato.promedioEquipo);
                                   return `${xPercent},${yPercent}`;
                                 }).join(' ')}
                               />
                             </svg>
                          </div>

                          {/* Capa 3: Puntos del promedio (z-30) */}
                          <div className="absolute inset-4 flex items-end justify-between z-30 pointer-events-none">
                             {datosMinutos.map((dato) => {
                               const posicionPromedio = calcularPosicionPromedio(dato.promedioEquipo);
                               return (
                                 <div key={`point-${dato.jornada}`} className="relative flex-1 flex flex-col items-center h-full pointer-events-auto">
                                    <div
                                      className="absolute w-3 h-3 bg-orange-500 rounded-full border-2 border-secondary-900 cursor-pointer hover:scale-125 transition-transform group/point"
                                      style={{ bottom: `calc(${posicionPromedio}% - 6px)` }}
                                    >
                                      {/* Tooltip del promedio */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover/point:opacity-100 transition-opacity bg-secondary-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none z-50">
                                        Promedio: {dato.promedioEquipo.toFixed(1)} min
                                      </div>
                                    </div>
                                 </div>
                               );
                             })}
                          </div>

                        </div>

                        {/* Etiquetas de jornadas */}
                        <div className="mt-4 w-full flex justify-between px-4">
                          {datosMinutos.map((dato) => (
                            <div key={`label-${dato.jornada}`} className="flex-1 text-center">
                              <span className="text-xs text-secondary-400 font-poppins">
                                J{dato.jornada}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Etiqueta del eje X */}
                <div className="text-center mt-8">
                  <span className="text-sm text-secondary-400 font-poppins">
                    Jornadas
                  </span>
                </div>
              </div>

              {/* Estadísticas resumen */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-secondary-800/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary-400 font-poppins">
                    {(datosMinutos.reduce((sum, dato) => sum + dato.minutosJugador, 0) / datosMinutos.length).toFixed(1)}
                  </div>
                  <div className="text-sm text-secondary-400 font-poppins mt-1">
                    Promedio del jugador
                  </div>
                </div>
                <div className="bg-secondary-800/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400 font-poppins">
                    {(datosMinutos.reduce((sum, dato) => sum + dato.promedioEquipo, 0) / datosMinutos.length).toFixed(1)}
                  </div>
                  <div className="text-sm text-secondary-400 font-poppins mt-1">
                    Promedio del equipo
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ModalMinutosJugador;